import React, { useRef, type ReactNode } from "react";

import { useAuth } from "@/lib/auth-context";
import {
  Bugsnag,
  notifyBugsnag,
  setBugsnagUser,
  startBugsnag,
} from "@/lib/bugsnag";

// Start at module load: `getPlugin('react')` only works after `start()`,
// and the ErrorBoundary class has to be created at component-definition
// time so it can be used in JSX below. `startBugsnag()` is idempotent
// and no-ops without `VITE_BUGSNAG_API_KEY`.
startBugsnag();

type FallbackProps = {
  error: Error;
  info: React.ErrorInfo;
  clearError: () => void;
};

function ErrorFallback({ clearError }: FallbackProps) {
  return (
    <section className="section">
      <div className="shell" style={{ textAlign: "center", maxWidth: 540 }}>
        <h1>Ceva nu a mers bine.</h1>
        <p>Am notat eroarea. Reîncearcă — dacă persistă, scrie-ne.</p>
        <button
          type="button"
          className="btn btn--primary btn--lg"
          onClick={() => {
            clearError();
            if (typeof window !== "undefined") window.location.reload();
          }}
        >
          Reîncarcă
        </button>
      </div>
    </section>
  );
}

// When the notifier key is missing the React plugin isn't registered, so
// fall back to a passthrough. Keeps the tree shape identical between
// configured and key-less environments.
const Plugin = Bugsnag.isStarted()
  ? Bugsnag.getPlugin("react") ?? null
  : null;
const ErrorBoundary = Plugin
  ? Plugin.createErrorBoundary(React)
  : ({ children }: { children?: ReactNode }) => <>{children}</>;

// Sync Firebase auth identity onto Bugsnag without `useEffect`. The
// "compare previous render" idiom from patterns.md keeps the call in
// the commit phase, not in a cascading effect.
function UserSync() {
  const { user } = useAuth();
  const prevUid = useRef<string | null | undefined>(undefined);
  const uid = user?.uid ?? null;
  if (uid !== prevUid.current) {
    prevUid.current = uid;
    setBugsnagUser(user);
  }
  return null;
}

export function BugsnagBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <UserSync />
      {children}
    </ErrorBoundary>
  );
}

export { notifyBugsnag };
